(() => {
  const bottomBarTag = 'kivo-home-product-bottom-bar';

  if (!customElements.get(bottomBarTag)) {
    customElements.define(
      bottomBarTag,
      class KivoHomeProductBottomBar extends HTMLElement {
        connectedCallback() {
          this.productTop = this.closest('.kivo-home-product-top');
          this.bottomButton = this.querySelector('.kivo-home-product-top__bottom-add');
          this.bottomPrice = this.querySelector('.kivo-home-product-top__bottom-price');
          this.bottomMedia = this.querySelector('.kivo-home-product-top__bottom-media');
          const mainButtonId = this.bottomButton?.dataset.mainButtonId;
          const mainButton = mainButtonId ? document.getElementById(mainButtonId) : null;
          const mainPrice = this.dataset.sectionId ? document.getElementById(`price-${this.dataset.sectionId}`) : null;
          this.mainButton = mainButton && this.productTop?.contains(mainButton) ? mainButton : null;
          this.mainPrice = mainPrice && this.productTop?.contains(mainPrice) ? mainPrice : null;

          if (!this.mainButton || !this.bottomButton) return;

          this.handleBottomButtonClick = () => {
            if (this.bottomButton.disabled) return;
            this.mainButton.click();
          };
          this.bottomButton.addEventListener('click', this.handleBottomButtonClick);

          // ProductInfo updates the existing price and submit button in place.
          // Mirror those nodes so this bar remains on Dawn's single product form.
          this.stateObserver = new MutationObserver(() => this.syncState());
          this.stateObserver.observe(this.mainButton, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true,
          });
          if (this.mainPrice) {
            this.stateObserver.observe(this.mainPrice, {
              attributes: true,
              childList: true,
              characterData: true,
              subtree: true,
            });
          }

          this.syncState();
          this.hidden = false;
          this.setVisible(false);

          if ('IntersectionObserver' in window) {
            this.visibilityObserver = new IntersectionObserver(([entry]) => {
              const hasPassedMainButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
              this.setVisible(hasPassedMainButton);
            });
            this.visibilityObserver.observe(this.mainButton);
          } else {
            this.handleViewportChange = () => {
              if (this.visibilityFrame) return;
              this.visibilityFrame = requestAnimationFrame(() => {
                this.visibilityFrame = null;
                this.setVisible(this.mainButton.getBoundingClientRect().bottom <= 0);
              });
            };
            window.addEventListener('scroll', this.handleViewportChange, { passive: true });
            window.addEventListener('resize', this.handleViewportChange);
            this.handleViewportChange();
          }
        }

        disconnectedCallback() {
          this.bottomButton?.removeEventListener('click', this.handleBottomButtonClick);
          this.stateObserver?.disconnect();
          this.visibilityObserver?.disconnect();
          if (this.handleViewportChange) {
            window.removeEventListener('scroll', this.handleViewportChange);
            window.removeEventListener('resize', this.handleViewportChange);
          }
          if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
        }

        setVisible(shouldShow) {
          this.classList.toggle('is-visible', shouldShow);
          this.toggleAttribute('inert', !shouldShow);
          if (shouldShow) {
            this.removeAttribute('aria-hidden');
          } else {
            this.setAttribute('aria-hidden', 'true');
          }
        }

        syncState(updatedHtml) {
          if (!this.mainButton || !this.bottomButton) return;

          const mainLabel = this.mainButton.querySelector('.kivo-home-product-top__add-label');
          const bottomLabel = this.bottomButton.querySelector('.kivo-home-product-top__bottom-add-label');
          if (mainLabel && bottomLabel) bottomLabel.textContent = mainLabel.textContent.trim();

          const isDisabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
          this.bottomButton.disabled = isDisabled;

          const opensDialog = this.mainButton.getAttribute('aria-haspopup');
          if (opensDialog) {
            this.bottomButton.setAttribute('aria-haspopup', opensDialog);
          } else {
            this.bottomButton.removeAttribute('aria-haspopup');
          }

          const isLoading = this.mainButton.classList.contains('loading');
          this.bottomButton.classList.toggle('loading', isLoading);
          this.bottomButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);

          if (this.mainPrice && this.bottomPrice) {
            if (this.bottomPrice.innerHTML !== this.mainPrice.innerHTML) {
              this.bottomPrice.innerHTML = this.mainPrice.innerHTML;
            }
            this.bottomPrice.classList.toggle('hidden', this.mainPrice.classList.contains('hidden'));
          }

          if (updatedHtml && this.bottomMedia) {
            const updatedMedia = updatedHtml.getElementById(`BottomBarMedia-${this.dataset.sectionId}`);
            if (updatedMedia && this.bottomMedia.innerHTML !== updatedMedia.innerHTML) {
              this.bottomMedia.innerHTML = updatedMedia.innerHTML;
            }
          }
        }
      }
    );
  }

  if (window.KivoHomeProductTopInitialized) return;
  window.KivoHomeProductTopInitialized = true;

  // Dawn restores its locale label after each variant fetch; reapply the
  // merchant-configured CTA copy once that standard update is complete.
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const productTop = Array.from(document.querySelectorAll('.kivo-home-product-top')).find(
      (section) => section.dataset.section === data.sectionId
    );
    if (!productTop) return;

    const submitButton = productTop.querySelector('.kivo-home-product-top__add');
    if (submitButton && !submitButton.disabled) {
      const label = submitButton.querySelector('.kivo-home-product-top__add-label');
      if (label && submitButton.dataset.addLabel) label.textContent = submitButton.dataset.addLabel;
    }

    productTop.querySelector(bottomBarTag)?.syncState(data.html);
  });
})();
